/** Shared types for the grid batch-actions foundation.
 *
 *  A `BatchActionDef` declares one action available from the grid's "Batch
 *  actions" menu. Three levels of customisation, in precedence order:
 *   1. `dialog(ctx)` — render a fully custom dialog (e.g. a form). Overrides
 *      the built-in confirm.
 *   2. `run(ctx)`    — a fully custom async handler (e.g. call an existing,
 *      non-generic endpoint). Used when `dialog` is absent.
 *   3. `action` + optional `confirmText` — the common case: a ConfirmDialog
 *      (destructive-styled when `destructive`) then a generic `/batch/` POST via
 *      `ctx.runStandard`.
 */
import type { ReactNode } from 'react';

import type { BatchSelection } from '@/api/batch';

/** Everything an action handler / custom dialog needs to act on the current
 *  grid selection. Supplied by ResourceDataGrid when the action fires. */
export interface BatchActionContext {
  /** `{mode:'ids',ids}` for a manual/page selection, `{mode:'all_matching'}`
   *  once "Select all matching" is engaged. */
  selection: BatchSelection;
  /** The number the user thinks they're acting on (ticked rows, or the grid
   *  total in all-matching mode) — for confirm copy. */
  count: number;
  /** Grid total for the current query, or null when the grid defers its count
   *  and no total has been resolved. Prefer `matchedPhrase` for prose. */
  matched: number | null;
  /** Ready-made subject phrase for all-matching confirmations: "all 1,234
   *  matching" — or just "all matching" when the total isn't known. Use it so
   *  dialog copy reads correctly on deferred-count grids:
   *  `Acting on {ctx.matchedPhrase} bookings.` */
  matchedPhrase: string;
  /** The grid's current list query string (search + filters + tenant +
   *  ordering, no leading `?`) — forward to the backend so `all_matching`
   *  resolves the same set the grid shows. */
  filterQuery: string;
  /** The grid's active tenant filter (null when unfiltered / the grid has no
   *  tenant dimension). Lets tenant-sensitive dialogs skip re-deriving it from
   *  `filterQuery` (whose param name varies per grid). */
  tenantFilterId: number | null;
  /** Tenant id per selected row, aligned with `selection.ids` (ids mode only;
   *  empty in all-matching mode). `null` marks a row that is NOT currently
   *  loaded (the selection crossed pages — `keepNonExistentRowsSelected`) or a
   *  record without a tenant, so `null` always means "tenant unknown here". */
  rowTenantIds: Array<number | null>;
  /** Close the action's dialog / dismiss the menu flow. */
  close: () => void;
  /** Clear the grid's row selection (and exit all-matching mode). `runStandard`
   *  calls this for you on success; a custom `run` handler should call it after
   *  its own success. */
  clearSelection: () => void;
  /** Fire the generic `/batch/` dispatch for this action's `action` key
   *  (snackbar + clear selection + invalidate handled for you). Rejects if the
   *  action has no `action` key. */
  runStandard: (params?: Record<string, unknown>) => Promise<void>;
}

export interface BatchActionDef {
  /** Stable key (also the React key in the menu). */
  key: string;
  /** Menu-item + confirm-button label. */
  label: string;
  icon?: ReactNode;
  /** Permission gating the action; hidden unless `hasPerm(permission)`. */
  permission?: string;
  /** Red/destructive styling in the menu + confirm dialog. */
  destructive?: boolean;
  /** Action key for the generic `/batch/` dispatch (via `runStandard`). */
  action?: string;
  /** Simple confirm-dialog body before `runStandard` (used when neither
   *  `dialog` nor `run` is given). Receives the acted-on count. */
  confirmText?: (n: number) => string;
  /** Prompt for a reason/comment in the built-in confirm dialog. `'required'`
   *  blocks confirm until non-empty (change-log resources); `'optional'`
   *  allows empty (then omitted from params entirely). */
  reason?: 'required' | 'optional';
  /** Params key the collected text is sent under (default `'reason'`). */
  reasonParam?: string;
  /** Label for the reason field (default `'Reason'`). */
  reasonLabel?: string;
  /** Custom dialog (overrides the built-in confirm). */
  dialog?: (ctx: BatchActionContext) => ReactNode;
  /** Fully custom handler (e.g. an existing endpoint). Used when `dialog` is
   *  absent; runs directly on click with no confirm. */
  run?: (ctx: BatchActionContext) => Promise<void>;
  /** Why the action can't run on this selection, or null when it can. An
   *  unavailable action stays in the menu, disabled, with the reason as its second
   *  line. Gets the selected rows the grid has loaded (a ticked row on another page
   *  is absent; none in all-matching mode, where the backend decides). */
  unavailableReason?: (rows: readonly unknown[]) => string | null;
  /** Opt-in: the server may refuse this action item by item (see BatchRefusals). */
  refusals?: BatchRefusals;
}

/** One item the server refused, as the refusal dialog lists it. */
export interface BatchRefusedItem {
  id: number | string;
  /** Why, in plain words ("In use"). */
  reason: string;
  /** The item's own page, when it has one. */
  to?: string;
}

/** A dispatch the server refused outright: nothing was queued. */
export interface BatchRefusal {
  /** The dialog's title ("Can’t delete the selected itineraries"). */
  title: string;
  /** The server's sentence. */
  detail: string;
  items: readonly BatchRefusedItem[];
}

/** How an action the server may refuse item by item reports it. The server leaves the
 *  refused items out of the task (the 202 counts only the rest) and answers 409 when it
 *  refuses them all. Without this, an action's 409 is the generic error snackbar and its
 *  202 says nothing about what was left out. */
export interface BatchRefusals {
  /** Words added to the "started" snackbar when the server left `n` items out of the task
   *  (`processed < matched`), or null when the cap cut the selection too, so the number
   *  left out is unknown. */
  leftOut: (n: number | null) => string;
  /** The refusal behind a failed dispatch, or null for any other error (which keeps the
   *  generic snackbar). */
  refused: (error: unknown) => BatchRefusal | null;
}

/** The universal Delete batch action, gated on the grid's manage permission.
 *  Dispatches the generic `delete` action asynchronously (background task) after
 *  a destructive confirm. Pass `{ reason: 'required' }` for resources whose
 *  deletion records a mandatory change-log reason (e.g. blacklist rules). */
export function deleteBatchAction(
  permission: string,
  opts: { reason?: 'required' } = {},
): BatchActionDef {
  return {
    key: 'delete',
    label: 'Delete',
    action: 'delete',
    destructive: true,
    permission,
    reason: opts.reason,
    confirmText: (n) =>
      `Delete ${n.toLocaleString()} record(s)? This runs in the background and can’t be undone.`,
  };
}
